import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { MenuStages } from '../constants/enums';
import { CartItemAdditionTypes } from '../constants/event';
import {
  Category,
  CategoryAndTimePeriodQueryQuery,
  MenuItem,
  MenuItemSetting,
  MenuItemsQueryQuery,
  MenuOverride,
  ModalityType,
  ModifierGroup,
  ModifierGroupsQueryQuery,
  PosProperties,
  TimePeriod,
} from '../generated-interfaces/graphql';
import { startLoading } from '../redux/features/loading/loading.slice';
import { IUnavailableItem } from '../types';
import { defaultMenuVersion } from '../utils/constants';
import {
  getModifierAndModGroupFromHypotheses,
  getModifierAndModGroupFromHypothesesByName,
} from '../utils/hypotheses';
import logger from '../utils/logger';
import { isExpressModeActive } from '../utils/expressMode';
import { ModSymbolCodeNameMappingType } from '../utils/mappings';
import {
  buildFullMenuItem,
  considerTimePeriodCategory,
  fetchBrandMenu,
  fetchMenuBasedOnStage,
  findCartItemInMenuGraphByName,
  formatCategoryAndTimePeriodResponse,
  formatMenuResponse,
  formatModGroupResponse,
  getComboPrpIdToALaCartePrpId,
  getEligibleAutoComboItem,
  getParentModifier,
  IMenuVersion,
  IMenuVersionsResponse,
  MenuResponses,
  parseCategoryAndTimeperiodResponse,
  ParsedCategory,
  parseMenuResponse,
  PersistentMenuProperty,
  TopLevelMenuItem,
} from '../utils/menu';
import { getMenuVersionsFromMenuAPI } from '../utils/network';
import { GenericMap } from '../utils/types';
import { addHypothesisCartItem, cartActions, setSequenceId } from './cartSlice';
import { dialogActions } from './dialogSlice';
import {
  AgentInterceptionType,
  EntityMenuItem,
  ErrorTransmissionMessage,
  messagingActions,
  TextFrameTransmissionMessage,
} from './messagingSlice';
import {
  CartItem,
  getCartItemNonDummyNode,
  getSelectedCartItem,
  getValidCartItems,
  processDefaultSelectedModifiers,
  selectModifierInCartItem,
} from '../utils/cart';
import {
  resetAutoComboState,
  setAutoCombinedCart,
} from '../redux/features/autoCombo/autoCombo.slice';
import { sendOrder } from './orderSlice';
import {
  getAuthRefactorFeatureFlag,
  getMenuUnificationFeatureFlag,
} from '../utils/session-storage';
import { processBrandMenu } from '../utils/menuUnification';
import {
  IBrandMenu,
  IBrandUnavailableEntity,
  IBusinessRule,
  IConstraint,
  IItemConversionBusinessRules,
  IMUItem,
  IRestaurantMealPeriod,
  IRestaurantUnavailableEntity,
  IUtterance,
} from '../types/brandMenu';
import { ParsedMenuItem } from '../types/menu';
import {
  buildSubstituteItem,
  buildFullMenuUnifiedItem,
  buildIntentCardsFromBM,
  getDismissRepeatPhrasesFromBM,
  getTopLevelItems,
  isBMItemOutOfStock,
  isBMModifierAnUnavailableConstraint,
} from '../utils/menuUnification.utils';
import { IntentCard } from '../types/intent';
import { upsell } from '../utils/upsell';
import { UpsellTriggers } from '../components/OmniBar/Omnibar.constants';
import { AUTO_COMBOS, UPSELL_METRIC_VERSION } from '../constants';
import { handleGreetingUpsell } from './menuSlice.utils';
import {
  initialItemConversionBusinessRules,
  LoadedVersionDetails,
} from './menuSlice.props';
import { getMenuVersion } from '../utils/menuVersion';
import { convertToMapUsingName } from '../utils';
import { analyzeOptionReplacement } from '../utils/optionReplacement';
import { ConversionSize } from '../utils/itemConversion.constants';

/**
 * Yields control back to the main thread so the browser can paint
 * pending UI updates (e.g. the loading spinner) before heavy
 * synchronous processing begins. Uses MessageChannel for minimal
 * latency (fires before setTimeout), with setTimeout(0) as fallback.
 */
const yieldToMain = (): Promise<void> =>
  new Promise((resolve) => {
    if (typeof MessageChannel !== 'undefined') {
      const channel = new MessageChannel();
      channel.port1.onmessage = () => resolve();
      channel.port2.postMessage(undefined);
    } else {
      setTimeout(resolve, 0);
    }
  });

export enum MenuLoadSource {
  CACHED = 'cached',
  DOWNLOADED = 'downloaded',
}

export interface MenuLoadingTimings {
  downloadStartTime?: number;
  downloadEndTime?: number;
  downloadDurationMs?: number;
  processingStartTime?: number;
  processingEndTime?: number;
  processingDurationMs?: number;
  source?: MenuLoadSource;
  unleashFlagDurationMs?: number;
}

export interface MenuState {
  topLevelMenuItems: GenericMap<TopLevelMenuItem>;
  availableCategoryWithTimePeriod: ParsedCategory[];
  categoriesWithTimePeriod: ParsedCategory[];
  alwaysAvailableCategories: ParsedCategory[];
  menuRes?: MenuResponses;
  fullMenuItems: GenericMap<ParsedMenuItem>;
  persistentVoiceProps: GenericMap<PersistentMenuProperty>;
  modSymbolMapping: ModSymbolCodeNameMappingType;
  codeNameMapping: ModSymbolCodeNameMappingType;
  menuVersions: IMenuVersion[];
  selectedMenuVersion: string;
  prodLiveVersion?: IMenuVersion;
  unavailableItems: Record<string, IUnavailableItem>;
  autoComboPrpIds: string[];
  comboPrpIdToALaCartePrpId: Record<string, string>;
  brandMenu?: IBrandMenu;
  brandMenuItemsByName: Record<string, IMUItem>;
  itemConversionBusinessRules: IItemConversionBusinessRules;
  restaurantUnavailableEntities: IRestaurantUnavailableEntity[];
  unavailableItemsByName: Record<
    string,
    Array<IRestaurantUnavailableEntity | IBrandUnavailableEntity>
  >;
  bmItemsSynonyms?: Record<string, Array<string>>;
  loadedVersionDetails: LoadedVersionDetails | null;
  restaurantMealPeriodsByName: Record<string, IRestaurantMealPeriod>;
  greetingDetails?: {
    item: any;
    constraint: IConstraint;
  };
  menuLoadingTimings: MenuLoadingTimings;
  intentCards?: IntentCard[];
  dismissRepeatPhrases?: string[];
}

export interface IAddItemToCart {
  menuItem: TopLevelMenuItem;
  quantity?: number;
  addedBy?: CartItemAdditionTypes;
  prefixWord?: string;
  inputModSymbol?: string;
  buildViaAutoCombo?: boolean;
  byPassAutoPrompt?: boolean;
}
export interface IComboItemMatch {
  autoComboPrpId: string;
  modifierGroupId: string;
  modifierPrpId: string;
  cartItemId: number;
  nonDummyItemPrpId?: string;
  nonDummyCartItemPrpId?: number;
}

export interface IProcessedCategoryAndTimePeriodJSON {
  categories: GenericMap<Category>;
  timePeriods: GenericMap<TimePeriod>;
}

export interface IProcessedMenuJSON {
  menuItems: GenericMap<MenuItem>;
  overrides: GenericMap<MenuOverride>;
  menuItemSettings: GenericMap<MenuItemSetting>;
  posSettings: GenericMap<PosProperties>;
}

export interface IProcessedModGroupsJSON {
  modifierGroups: GenericMap<ModifierGroup>;
}
export interface IUpdateMenuItems {
  topLevelMenuItems: GenericMap<TopLevelMenuItem>;
  processedCategoryAndTimePeriodJSON: GenericMap<IProcessedCategoryAndTimePeriodJSON>;
  processedMenuJSON: GenericMap<IProcessedMenuJSON>;
  processedModGroupsJSON: GenericMap<IProcessedModGroupsJSON>;
  categoriesWithTimePeriod: ParsedCategory[];
  availableCategoryWithTimePeriod: ParsedCategory[];
  alwaysAvailableCategories: ParsedCategory[];
  persistentVoiceProps: GenericMap<PersistentMenuProperty>;
  codeNameMapping: ModSymbolCodeNameMappingType;
  modSymbolMapping: ModSymbolCodeNameMappingType;
  unavailableItems: Record<string, IUnavailableItem>;
  restaurantMealPeriodsByName?: Record<string, IRestaurantMealPeriod>;
  itemConversionBusinessRules?: IItemConversionBusinessRules;
  brandMenu?: IBrandMenu;
  bmItemsSynonyms?: Record<string, Array<string>>;
  restaurantUnavailableEntities?: IRestaurantUnavailableEntity[];
  unavailableItemsByName?: Record<
    string,
    Array<IRestaurantUnavailableEntity | IBrandUnavailableEntity>
  >;
  restaurantCode?: string;
}

export const initialState: MenuState = {
  topLevelMenuItems: {},
  availableCategoryWithTimePeriod: [],
  categoriesWithTimePeriod: [],
  alwaysAvailableCategories: [],
  menuRes: undefined,
  fullMenuItems: {},
  persistentVoiceProps: {},
  modSymbolMapping: {},
  codeNameMapping: {},
  menuVersions: [],
  selectedMenuVersion: 'latest',
  prodLiveVersion: {} as IMenuVersion,
  unavailableItems: {},
  autoComboPrpIds: [],
  comboPrpIdToALaCartePrpId: {},
  brandMenu: undefined,
  brandMenuItemsByName: {},
  itemConversionBusinessRules: initialItemConversionBusinessRules,
  bmItemsSynonyms: undefined,
  restaurantUnavailableEntities: [],
  unavailableItemsByName: {},
  loadedVersionDetails: null,
  restaurantMealPeriodsByName: {},
  greetingDetails: undefined,
  menuLoadingTimings: {},
  intentCards: undefined,
  dismissRepeatPhrases: undefined,
};

/**
 * This function takes the menu data returned from APIs and make it in usable fashion to be stored in redux state
 */
const processMenuResponse = ({
  categoryAndTimePeriodJSON,
  menuJSON,
  modifierGroupJSON,
  persistentMenuProperty,
  unavailableItems,
  timezone,
  currentStage,
  getState,
}: {
  categoryAndTimePeriodJSON: CategoryAndTimePeriodQueryQuery | {};
  menuJSON: MenuItemsQueryQuery | {};
  modifierGroupJSON: ModifierGroupsQueryQuery | {};
  persistentMenuProperty: PersistentMenuProperty[];
  unavailableItems: Record<string, IUnavailableItem>;
  timezone: string;
  currentStage: string;
  getState: Function;
}) => {
  const {
    restaurant: { selectedRestaurantCode } = { selectedRestaurantCode: '' },
  } = getState();
  let persistentVoiceProps = {};
  try {
    if (persistentMenuProperty?.length > 0) {
      Object.assign(
        persistentVoiceProps,
        (persistentMenuProperty as PersistentMenuProperty[]).reduce(
          (acc, entry) => {
            acc[entry.unique_identifier] = entry;
            return acc;
          },
          {} as GenericMap<PersistentMenuProperty>
        )
      );
    }
  } catch (error) {
    logger.error({
      restaurantCode: selectedRestaurantCode,
      message: 'Failed while processing persistent menu properties',
      error,
    });
  }

  const processedCategoryAndTimePeriodJSON =
    formatCategoryAndTimePeriodResponse(
      categoryAndTimePeriodJSON as CategoryAndTimePeriodQueryQuery
    );

  const {
    categoriesWithTimePeriod,
    alwaysAvailableCategoryWithTimePeriods,
    autoComboPrpIds,
  } = parseCategoryAndTimeperiodResponse(processedCategoryAndTimePeriodJSON);

  const categoriesWithActiveTimePeriod = considerTimePeriodCategory(
    categoriesWithTimePeriod,
    timezone
  );
  const availableCategoryWithTimePeriod = categoriesWithActiveTimePeriod.filter(
    (category) => !category.inactive
  );

  const alwaysAvailableCategories = considerTimePeriodCategory(
    alwaysAvailableCategoryWithTimePeriods,
    timezone
  );

  const processedMenuJSON = formatMenuResponse(menuJSON as MenuItemsQueryQuery);

  const { topLevelMenuItems, codeNameMapping, modSymbolMapping } =
    parseMenuResponse({
      categories: [
        ...categoriesWithActiveTimePeriod.filter(
          (category) => category.name !== AUTO_COMBOS
        ),
        ...alwaysAvailableCategories,
      ],
      menuRes: processedMenuJSON,
      persistentVoiceProps,
      unavailableItems,
      stage: currentStage,
    });

  const modality = getState().cart.modality;
  const processedModGroupsJSON = formatModGroupResponse(
    modifierGroupJSON as ModifierGroupsQueryQuery
  );

  const comboPrpIdToALaCartePrpId = getComboPrpIdToALaCartePrpId(
    autoComboPrpIds,
    {
      ...processedCategoryAndTimePeriodJSON,
      ...processedMenuJSON,
      ...processedModGroupsJSON,
    }
  );

  return {
    modality,
    topLevelMenuItems,
    processedCategoryAndTimePeriodJSON,
    processedMenuJSON,
    processedModGroupsJSON,
    categoriesWithTimePeriod,
    availableCategoryWithTimePeriod,
    alwaysAvailableCategories,
    persistentVoiceProps,
    codeNameMapping,
    modSymbolMapping,
    unavailableItems,
    autoComboPrpIds,
    comboPrpIdToALaCartePrpId,
    restaurantCode: selectedRestaurantCode,
    itemConversionBusinessRules: initialItemConversionBusinessRules,
  };
};

export const fetchMenu = createAsyncThunk(
  'menu/getMenu',
  async (
    {
      restaurantCode,
      timezone,
      currentMenuVersion,
      currentStage,
      logout,
      taskRouterMenuLoadFailureCallback,
      taskRouterMenuLoadSuccessCallback,
    }: {
      restaurantCode: string;
      timezone?: string;
      currentMenuVersion: string;
      currentStage: MenuStages;
      logout: Function;
      taskRouterMenuLoadFailureCallback?: Function;
      taskRouterMenuLoadSuccessCallback?: Function;
    },
    { getState, dispatch }
  ) => {
    dispatch(updateLoadedVersionDetails(null));
    dispatch(resetMenuLoadingTimings());

    try {
      if (getMenuUnificationFeatureFlag()) {
        // Track download time
        const downloadStartTime = performance.now();
        dispatch(
          updateMenuLoadingTimings({
            downloadStartTime,
            source: MenuLoadSource.DOWNLOADED,
          })
        );

        const {
          brandMenu,
          restaurantUnavailableEntities,
          restaurantMealPeriods,
          restaurantCode: primaryRestaurantCode,
          utterancesPromise,
        } = await fetchBrandMenu({
          restaurantCode,
          state: getState(),
          dispatch,
        });

        const downloadEndTime = performance.now();
        const downloadDurationMs = downloadEndTime - downloadStartTime;
        dispatch(
          updateMenuLoadingTimings({ downloadEndTime, downloadDurationMs })
        );

        // Yield to the main thread so the loading spinner can render
        // before the heavy synchronous processing blocks the thread
        await yieldToMain();

        // Track processing time
        const processingStartTime = performance.now();
        dispatch(updateMenuLoadingTimings({ processingStartTime }));

        const processedMenu = processBrandMenu(
          brandMenu,
          primaryRestaurantCode,
          restaurantUnavailableEntities,
          restaurantMealPeriods,
          timezone
        );

        const processingEndTime = performance.now();
        const processingDurationMs = processingEndTime - processingStartTime;
        dispatch(
          updateMenuLoadingTimings({ processingEndTime, processingDurationMs })
        );

        dispatch(updateMenuItems(processedMenu));
        taskRouterMenuLoadSuccessCallback?.();

        // Load intent cards asynchronously — not on the critical path
        if (utterancesPromise) {
          utterancesPromise.then((utterances) => {
            if (utterances?.length) {
              dispatch(updateIntentCards(utterances));
            }
          });
        }
      } else {
        // Track download time
        const downloadStartTime = performance.now();
        dispatch(
          updateMenuLoadingTimings({
            downloadStartTime,
            source: MenuLoadSource.DOWNLOADED,
          })
        );

        const {
          categoryAndTimePeriodJSON,
          menuJSON,
          modifierGroupJSON,
          persistentMenuProperty,
          unavailableItems,
        } = await fetchMenuBasedOnStage({
          restaurantCode,
          state: getState(),
          currentMenuVersion,
          currentStage,
          dispatch,
        });

        const downloadEndTime = performance.now();
        const downloadDurationMs = downloadEndTime - downloadStartTime;
        dispatch(
          updateMenuLoadingTimings({ downloadEndTime, downloadDurationMs })
        );

        // Yield to the main thread so the loading spinner can render
        // before the heavy synchronous processing blocks the thread
        await yieldToMain();

        // Track processing time
        const processingStartTime = performance.now();
        dispatch(updateMenuLoadingTimings({ processingStartTime }));

        const processedMenu = processMenuResponse({
          categoryAndTimePeriodJSON,
          menuJSON,
          modifierGroupJSON,
          persistentMenuProperty,
          unavailableItems,
          timezone: timezone || '',
          currentStage,
          getState,
        });

        const processingEndTime = performance.now();
        const processingDurationMs = processingEndTime - processingStartTime;
        dispatch(
          updateMenuLoadingTimings({ processingEndTime, processingDurationMs })
        );

        dispatch(updateMenuItems(processedMenu));
        taskRouterMenuLoadSuccessCallback?.();
      }
    } catch (error) {
      const {
        cachedMenu: { menusById },
        taskRouter: { currentTaskId },
        order: { currentSessionId: orderSessionId },
        messages: { currentSessionId: messageSessionId },
      } = getState();

      logger.error({
        restaurantCode,
        message: 'Get Menu Items From Restaurant Portal Failed',
        isTaskFromTaskRouter: !!currentTaskId,
        error: JSON.stringify(error),
      });

      //If fetching the menu of a restaurant fails, as a fallback mechanism the following things are done when task is arrived via task-router
      // - Old menu cache of restaurant exists => use the old version menu
      // - Old menu cache of restaurant does not exists => trigger an intervention with reason code, empty the menu, alert of menu load error(5 seconds), take them to the task router lobby

      if (currentTaskId) {
        //Task arrived via task router
        if (menusById?.[restaurantCode]) {
          //Old menu cache exists for this restaurant
          logger.error({
            restaurantCode,
            message: 'Using old cache of the restaurant',
            isTR: true,
            error,
          });
          const {
            categoryAndTimePeriodJSON,
            menuJSON,
            modifierGroupJSON,
            persistentMenuProperty,
            unavailableItems,
          } = menusById[restaurantCode];
          const processedMenu = processMenuResponse({
            categoryAndTimePeriodJSON,
            menuJSON,
            modifierGroupJSON,
            persistentMenuProperty,
            unavailableItems,
            timezone: timezone || '',
            currentStage,
            getState,
          });
          dispatch(updateMenuItems(processedMenu));
          taskRouterMenuLoadSuccessCallback?.();
        } else {
          //No old cache exists for the restaurant
          logger.error({
            restaurantCode,
            message: 'No old cache exists for the restaurant',
            isTR: true,
            error,
          });
          taskRouterMenuLoadFailureCallback?.({
            restaurantCode,
            orderSessionId,
            messageSessionId,
            logout,
          });
        }
      } else if (getAuthRefactorFeatureFlag()) {
        logout('Error while fetching menu. Logging out');
      }
    }

    const versionDetails = getMenuVersion(getState);
    logger.log({
      messsage: `Set loaded menu version details`,
      versionDetails,
    });
    dispatch(updateLoadedVersionDetails(versionDetails));
  }
);

export const formAutoCombos = createAsyncThunk(
  '',
  async (_, { dispatch, getState }) => {
    try {
      const {
        menu: {
          menuRes,
          topLevelMenuItems,
          modSymbolMapping,
          autoComboPrpIds,
          persistentVoiceProps,
          unavailableItems,
        },
        cart: { cartItems, cartItemsQuantity, sequenceId, modality },
        config: { FEATURE_FLAG_AUTO_COMBO },
        restaurant: { selectedStage },
      } = getState();
      if (!FEATURE_FLAG_AUTO_COMBO) return;
      if (!menuRes) return;

      const validCartItems = Object.values(getValidCartItems(cartItems));
      // Need at least two valid cart items to create a combo item so stop finding auto combo when there is no valid cart item or the only valid cart item with quantity less than 2
      if (
        !validCartItems.length ||
        (validCartItems.length === 1 &&
          cartItemsQuantity[validCartItems[0].cartItemId] < 2)
      ) {
        dispatch(resetAutoComboState());

        // Trigger sendOrder after resetting auto combo state
        if (validCartItems.length) dispatch(sendOrder({}));
        return;
      }

      const autoCombinedCartItems = Object.assign({}, cartItems);
      const autoCombinedCartItemsQuantity = Object.assign(
        {},
        cartItemsQuantity
      );
      let currSequenceId = sequenceId;

      // Use while loop with explicit index control to allow restarting from beginning
      // when a combo is created (cart state changes)
      let i = 0;
      while (i < autoComboPrpIds.length) {
        // Need at least two valid cart items to create a combo item so stop finding auto combo when there is no valid auto combined cart item or one valid auto combined cart item with quantity less than 2
        const validAutoCombinedCartItems = getValidCartItems(
          autoCombinedCartItems
        );
        const validAutoCombinedCartItemsToArray = Object.values(
          validAutoCombinedCartItems
        );
        if (
          !validAutoCombinedCartItemsToArray.length ||
          (validAutoCombinedCartItemsToArray.length === 1 &&
            autoCombinedCartItemsQuantity[
              validAutoCombinedCartItemsToArray[0].cartItemId
            ] < 2)
        )
          break;

        const autoComboPrpId = autoComboPrpIds[i];
        const cartCopy = Object.assign({}, validAutoCombinedCartItems);
        const cartItemsQuantityCopy = Object.assign(
          {},
          autoCombinedCartItemsQuantity
        );
        const autoCombo = menuRes.menuItems[autoComboPrpId] || {};

        // Check the cart to find the eligible auto combo item
        const {
          isAutoComboEligible,
          comboItemMatches,
          isDummyItem,
          nonDummyItemMatch,
        } = getEligibleAutoComboItem(
          autoCombo,
          menuRes,
          autoComboPrpId,
          cartItemsQuantityCopy,
          cartCopy
        );

        if (isAutoComboEligible && comboItemMatches.length) {
          logger.log(`Find eligible auto combo ${autoCombo.name}`);
          // populate the full menu graph
          const menuItem = topLevelMenuItems[autoComboPrpId];
          if (!menuItem) {
            logger.log(
              `can not find the ${autoCombo.name} with id ${autoComboPrpId} in the menu`
            );
            i++; // Increment before continue to avoid infinite loop
            continue;
          }
          const parsedMenuItem = buildFullMenuItem(
            menuItem,
            menuRes,
            persistentVoiceProps,
            unavailableItems,
            selectedStage
          );
          const cartItem: CartItem = {
            ...parsedMenuItem,
            cartItemId: currSequenceId,
            childModifierGroups: {},
            modality,
            modcode: menuItem.modcode,
            addedBy: CartItemAdditionTypes.human,
            autoCombinedCartItemIds: [],
          };

          logger.log(`Start building auto combined cart item ${cartItem.name}`);
          // Add the auto combo item to autoCombinedCartItems
          autoCombinedCartItems[currSequenceId] = cartItem;
          autoCombinedCartItemsQuantity[currSequenceId] = 1;

          let node = cartItem;
          if (isDummyItem && nonDummyItemMatch) {
            const nonDummyItemParentModGroup = Object.values(
              parsedMenuItem.modifierGroups
            )[0];
            const nonDummyItem = Object.values(parsedMenuItem.modifierGroups)[0]
              .menuItems[nonDummyItemMatch.id];
            // select the non-dummy item first and use it as the node to populate the items
            logger.log(
              `Select the non dummy item ${nonDummyItem.name} under modifier group ${nonDummyItemParentModGroup.name} in the auto combo ${cartItem.name}`
            );
            selectModifierInCartItem(
              cartItem,
              nonDummyItem.itemId,
              nonDummyItemParentModGroup.id,
              true,
              '',
              modSymbolMapping,
              menuRes.posSettings,
              modality
            );
            node =
              cartItem.childModifierGroups[nonDummyItemParentModGroup.id]
                ?.selectedItems[nonDummyItemMatch.id];
            logger.log(
              `Change auto combined cart item node to ${node.name} for dummy item`
            );
          }

          // Add combo items as modifiers and remove them from the cart
          comboItemMatches.forEach((match) => {
            autoCombinedCartItems[currSequenceId].autoCombinedCartItemIds?.push(
              match.cartItemId
            );

            const comboItemParentModGroup =
              node.modifierGroups[match.modifierGroupId];

            const comboItem =
              comboItemParentModGroup?.menuItems[match.modifierPrpId];
            if (!comboItemParentModGroup || !comboItem) {
              logger.log(
                `Can not find matched combo item ${comboItem?.name} or matched parent modifier group ${comboItemParentModGroup.name} when forming the auto combo`
              );
              throw new Error(
                `Can not find matched combo item ${comboItem?.name} or matched parent modifier group ${comboItemParentModGroup?.name} when forming the auto combo`
              );
            }

            let pathToItem = comboItemParentModGroup.id;
            // Update the cartItem directly
            logger.log(
              `Select the matched combo item ${comboItem.name} under modifier group ${comboItemParentModGroup.name} in ${node.name}`
            );
            selectModifierInCartItem(
              node,
              comboItem.itemId,
              comboItemParentModGroup.id,
              true,
              '',
              modSymbolMapping,
              menuRes.posSettings,
              modality,
              pathToItem
            );

            const handleModifierMatch = (
              cartItemMatch: CartItem,
              comboItem: ParsedMenuItem,
              pathToItem: string
            ) => {
              logger.log(
                'Handle auto combo modifier match: ',
                cartItemMatch.name,
                comboItem.name,
                pathToItem
              );
              const cartItemMatchChildrenModGroupIds = Object.keys(
                cartItemMatch.childModifierGroups
              );
              if (cartItemMatchChildrenModGroupIds.length) {
                const comboItemModGroupIds = Object.keys(
                  comboItem.modifierGroups
                );
                cartItemMatchChildrenModGroupIds.forEach((childModGroupId) => {
                  Object.values(
                    cartItemMatch.childModifierGroups[childModGroupId]
                      .selectedItems
                  ).forEach((cartItemSelectedItem) => {
                    let menuItemMatchId;
                    let parentModGroupMatchId;
                    if (comboItemModGroupIds.includes(childModGroupId)) {
                      // combo item and a la cart item are using the same modifier group
                      menuItemMatchId = cartItemSelectedItem.id;
                      parentModGroupMatchId = childModGroupId;
                    } else {
                      // combo item and a la cart item are not using the same modifier group. Try to match by name.
                      const { menuItemMatchByNameId, modGroupMatchByNameId } =
                        findCartItemInMenuGraphByName(
                          cartItemSelectedItem,
                          comboItem
                        );
                      menuItemMatchId = menuItemMatchByNameId;
                      parentModGroupMatchId = modGroupMatchByNameId;
                    }
                    if (menuItemMatchId && parentModGroupMatchId) {
                      const modGroup =
                        comboItem.modifierGroups[parentModGroupMatchId];
                      const menuItem = modGroup.menuItems[menuItemMatchId];
                      const pathToModifier = [
                        pathToItem,
                        comboItem.id,
                        modGroup.id,
                      ].join('__');
                      logger.log(
                        `Select the matched modifier ${menuItem.name} under modifier group ${modGroup.name} in ${node.name}`
                      );
                      selectModifierInCartItem(
                        node,
                        menuItem.itemId,
                        modGroup.id,
                        true,
                        '',
                        modSymbolMapping,
                        menuRes.posSettings,
                        modality,
                        pathToModifier
                      );
                      handleModifierMatch(
                        cartItemSelectedItem,
                        menuItem,
                        pathToItem
                      );
                    } else {
                      logger.log(
                        `Can not find matched combo item nested modifier ${cartItemSelectedItem?.name} or matched combo item nested modifier group id ${childModGroupId} when forming the auto combo`
                      );
                    }
                  });
                });
              }
            };

            if (autoCombinedCartItems[match.cartItemId]) {
              handleModifierMatch(
                getCartItemNonDummyNode(
                  autoCombinedCartItems[match.cartItemId]
                ),
                comboItem,
                pathToItem
              ); // Handle nested modifiers
            } else {
              logger.log(
                `Can not find the matched cartItemId ${match.cartItemId}`
              );
            }

            const quantity = autoCombinedCartItemsQuantity[match.cartItemId];
            if (quantity > 1) {
              autoCombinedCartItemsQuantity[match.cartItemId] -= 1;
            } else {
              delete autoCombinedCartItems[String(match.cartItemId)];
              delete autoCombinedCartItemsQuantity[match.cartItemId];
            }
          });
          currSequenceId += 1;
          dispatch(setSequenceId(currSequenceId)); // Update the sequence id in cart state to avoid duplicate cart item id between auto combo items and cart items
          // Restart from beginning since cart state changed (items were removed)
          i = 0;
        } else {
          // Only increment if no combo was created
          i++;
        }
      }

      dispatch(
        setAutoCombinedCart({
          autoCombinedCartItems,
          autoCombinedCartItemsQuantity,
        })
      );

      // Trigger sendOrder after forming auto combo and there is at least one valid cart item
      const validAutoCombinedCartItems = Object.values(
        getValidCartItems(autoCombinedCartItems)
      );
      if (validAutoCombinedCartItems.length) dispatch(sendOrder({}));

      // Update the select item to be the regular cart item before auto combined item as it's not selectable
      const items = Object.values(autoCombinedCartItems);
      const firstAutoCombinedCartItemIdx = items.findIndex(
        (item) =>
          item.autoCombinedCartItemIds &&
          item.autoCombinedCartItemIds.length > 0
      );

      if (firstAutoCombinedCartItemIdx >= 0) {
        const selectedIndex =
          firstAutoCombinedCartItemIdx === 0
            ? 0
            : firstAutoCombinedCartItemIdx - 1;
        const selectedItem = items[selectedIndex];
        dispatch(
          dialogActions.selectItemAndResetNavigation({
            item: selectedItem,
            itemCartId: selectedItem.cartItemId,
          })
        );
      }
    } catch (error) {
      logger.error({
        message: 'Form Auto Combo Failed',
        error: JSON.stringify(error),
      });
    }
  }
);

export const addItemToCart = createAsyncThunk(
  'menu/addItemToCart',
  async (
    {
      menuItem,
      quantity,
      addedBy = CartItemAdditionTypes.human,
      prefixWord,
      inputModSymbol,
      buildViaAutoCombo = false,
      byPassAutoPrompt = false,
    }: IAddItemToCart,
    { dispatch, getState }
  ) => {
    const {
      cart: { modality, sequenceId: cartSequenceId },
      menu: {
        menuRes,
        persistentVoiceProps,
        modSymbolMapping,
        unavailableItems,
        brandMenu,
        brandMenuItemsByName,
        unavailableItemsByName,
        restaurantMealPeriodsByName,
        greetingDetails,
      },
      restaurant: {
        selectedStage,
        selectedRestaurantDetails: {
          restaurantCode = '',
          restaurantName = '',
          timezone,
        },
        isAIEnabled,
      },
    } = getState();

    const isUnifiedMenu = getMenuUnificationFeatureFlag();

    if (menuRes) {
      const parsedMenuItem = isUnifiedMenu
        ? buildFullMenuUnifiedItem({
            item: menuItem,
            brandMenu,
            restaurantCode,
            unavailableItemsByName,
            buildViaAutoCombo,
            timezone,
            restaurantMealPeriodsByName,
            brandMenuItemsByName,
          })
        : buildFullMenuItem(
            menuItem,
            menuRes,
            persistentVoiceProps,
            unavailableItems,
            selectedStage
          );
      const cartItem = {
        ...parsedMenuItem,
        cartItemId: cartSequenceId,
        childModifierGroups: {},
        modality,
        modcode: menuItem.modcode,
        addedBy,
        ...(isUnifiedMenu ? { readback: true } : null),
        ...(buildViaAutoCombo ? { isAutoCombined: true } : null),
        byPassAutoPrompt,
      };

      // handle default selected modifier
      processDefaultSelectedModifiers(
        cartItem,
        modSymbolMapping,
        menuRes.posSettings,
        modality,
        unavailableItemsByName
      );

      // If the item being added is the first one and greeting has an upsell item
      if (
        isUnifiedMenu &&
        addedBy === CartItemAdditionTypes.human &&
        cartSequenceId === 1 &&
        greetingDetails?.constraint?.is_upsell_prompt
      ) {
        handleGreetingUpsell(
          greetingDetails,
          menuItem,
          !!isAIEnabled,
          dispatch,
          upsell
        );
      }

      const {
        config: {
          FEATURE_FLAG_SUPPRESS_UPSELL: suppressUpsellEnabled,
          FEATURE_FLAG_DO_NOT_UPSELL_IN_EXPRESS_MODE:
            doNotUpsellInExpressModeEnabled,
        },
      } = getState();
      const inExpressMealPeriod = isExpressModeActive(
        restaurantMealPeriodsByName,
        timezone
      );
      const suppressUpsellsInExpressMode =
        doNotUpsellInExpressModeEnabled &&
        addedBy !== CartItemAdditionTypes.AI &&
        inExpressMealPeriod;
      logger.log(
        `[EXPRESS_MODE] addItemToCart "${cartItem.name}" (cartItemId=${cartItem.cartItemId}, addedBy=${addedBy}): suppressUpsellsInExpressMode=${suppressUpsellsInExpressMode}, doNotUpsellInExpressModeEnabled=${doNotUpsellInExpressModeEnabled}, inExpressMealPeriod=${inExpressMealPeriod}`
      );

      dispatch(
        cartActions.addItemToCart({
          cartItem,
          suppressUpsellsInExpressMode,
          suppressUpsellEnabled,
        })
      );

      dispatch(
        dialogActions.selectItemAndResetNavigation({
          item: cartItem,
          itemCartId: cartSequenceId,
        })
      );

      if (isUnifiedMenu) {
        // Check upsell and auto reject it if the cart context changes
        upsell.settle();
      }

      if (quantity) {
        dispatch(
          cartActions.updateQuantity({
            cartItemId: String(cartSequenceId),
            quantity: Math.min(quantity, 19),
          })
        );
      }

      if (prefixWord) {
        // handle prefixWord by selecting it as modifier if it is found
        Object.values(parsedMenuItem.modifierGroups).every((modGroup) => {
          const target = Object.values(modGroup.menuItems).find(
            (menuItem) =>
              menuItem.name.toLowerCase() === prefixWord.toLowerCase()
          );
          if (target) {
            dispatch(
              cartActions.selectModifier({
                cartItemId: cartSequenceId,
                menuItemId: target.itemId,
                modGroupId: modGroup.id,
                selected: true,
                modCode: inputModSymbol || '',
                modSymbolMapping,
                posSettings: menuRes.posSettings,
                suppressUpsellEnabled,
                applyExpressCascade: suppressUpsellsInExpressMode,
              })
            );
            return false;
          }
          return true;
        });
      }

      dispatch(setFullMenuItem(parsedMenuItem));

      // Upsell attempt when a upsell item is added to the cart
      if (isUnifiedMenu && cartItem.name.includes('/upsell')) {
        let [intent, sendVal] = cartItem.name.replace('/', '').split(':');
        if (!sendVal && menuItem !== undefined) {
          const { slashCommands, slashCommandIdx, name } = menuItem;
          if (menuItem !== undefined && slashCommands) {
            sendVal = slashCommands[slashCommandIdx || 0];
            const newIndex =
              slashCommandIdx === slashCommands.length - 1
                ? 0
                : (slashCommandIdx || 0) + 1;
            dispatch(
              updateItemSlashCommandIdx({ itemName: name, newIndex: newIndex })
            );
            sendVal = slashCommands[slashCommandIdx || 0];
          }
        }
        const constraints = Object.values(cartItem.modifierGroups);
        let upsell_attempted;
        if (constraints.length > 0) {
          upsell_attempted = upsell.attempt(
            constraints[0],
            '',
            UpsellTriggers.hitlOmnibar,
            sendVal,
            intent
          );
        }
        if (!byPassAutoPrompt) {
          sendVal = sendVal.replaceAll('{RESTAURANT_NAME}', restaurantName);
          const payload: Partial<TextFrameTransmissionMessage> = {
            data: {
              payload: sendVal,
              status: 'final',
              metadata: {
                intent,
                upsell_attempted,
                upsell_metric_version: UPSELL_METRIC_VERSION,
              },
            },
          };
          dispatch(messagingActions.sendMessage(payload as any));
          dispatch(
            messagingActions.sendAgentInterception({
              data: {
                type: AgentInterceptionType.TTS_SENT,
              },
            })
          );
        }
      }
    }
  }
);

export const addHypothesisItemToCart = createAsyncThunk(
  'menu/addHypothesisItemToCart',
  async (
    {
      orderItem,
      skipFindInMenu,
    }: { orderItem: EntityMenuItem; skipFindInMenu: boolean },
    { dispatch, getState }
  ) => {
    const {
      menu: {
        topLevelMenuItems,
        modSymbolMapping,
        codeNameMapping,
        menuRes,
        unavailableItemsByName,
      },
      cart: { sequenceId: cartSequenceId },
      config: {
        FEATURE_FLAG_BM_AUTO_COMBO,
        FEATURE_FLAG_SUPPRESS_UPSELL: suppressUpsellEnabled,
      },
    } = getState();

    const isUnifiedMenu = getMenuUnificationFeatureFlag();

    if (skipFindInMenu) {
      // No need to build the cart tree and just display the brand menu cart when HITL and AI are using different menu
      logger.log(
        `Add dummy hypothesis item: ${orderItem.name} to cart with cart item id: ${cartSequenceId} without finding it in the menu`
      );
      dispatch(
        addHypothesisCartItem({
          ...orderItem,
          cartItemId: cartSequenceId,
        })
      );
      return;
    }

    const selectChildMod = ({
      mod,
      fullMainItem,
      currentCartItemId,
      modModifierGroupId,
      modQuantity,
    }: {
      mod: EntityMenuItem;
      fullMainItem: ParsedMenuItem | undefined;
      currentCartItemId: number;
      modModifierGroupId: string;
      modQuantity: number;
    }) => {
      if (!fullMainItem) {
        logger.error(
          `Cannot process modifier: fullMainItem is undefined for modifier group ${modModifierGroupId}`
        );
        return;
      }

      //find the modifier group and modifier based on the modifier id
      const modifierId = mod.id !== -1 ? String(mod.id) : mod.name || '';
      if (!modifierId) {
        logger.error(
          `Cannot find modifier: mod.id is -1 and mod.name is undefined for modifier group ${modModifierGroupId}`
        );
        return;
      }

      let { modGroup, modifier } = getModifierAndModGroupFromHypotheses(
        fullMainItem,
        modifierId,
        modModifierGroupId
      );

      if (!modGroup || !modifier) {
        //find the modifier group and modifier based on the modifier name if they couldn't be found by id
        logger.error(
          `Can't find the modifier ${modifierId} or modifier group ${modModifierGroupId} based on the ids from the hypotheses in the menu`
        );

        if (mod.name) {
          const { modGroup: modGroupByName, modifier: modifierByName } =
            getModifierAndModGroupFromHypothesesByName(
              fullMainItem,
              mod.name,
              modModifierGroupId
            );
          modGroup = modGroupByName;
          modifier = modifierByName;
        }
      }

      if (modifier && modGroup) {
        const modCode = mod.pos_specific_properties?.modcode
          ? codeNameMapping[mod.pos_specific_properties.modcode]?.code
          : '';

        const cartItems = getState().cart.cartItems;
        const selectedCartItem = getSelectedCartItem(cartItems);

        const pathToItem = modifier.pathToItem || '';
        let shouldAddModifier = true;
        if (
          isUnifiedMenu &&
          selectedCartItem &&
          isBMModifierAnUnavailableConstraint(
            selectedCartItem,
            unavailableItemsByName,
            getParentModifier(pathToItem),
            pathToItem,
            modGroup.id,
            modifier.itemId
          )
        ) {
          shouldAddModifier = false;
          logger.error(
            `Not adding to cart as the item ${modifier.itemId} is unavailable`
          );
        }

        if (shouldAddModifier) {
          //TODO handle the quantity of modifier when we have the quantity selection function in the future. Now the quantity should default to 1.
          dispatch(
            cartActions.selectModifier({
              cartItemId: currentCartItemId,
              menuItemId: modifier.itemId,
              modGroupId: modGroup.id,
              selected: true,
              modCode,
              modSymbolMapping,
              posSettings: menuRes?.posSettings || {},
              quantity: modQuantity,
              readback: mod.readback || false,
              pathToItem: modifier.pathToItem,
              suppressUpsellEnabled,
            })
          );

          // Handle nested modifier
          if (mod.children && mod.children.length > 0) {
            handleOrderItemChildren(mod.children, modifier, currentCartItemId);
          }
        }
      } else {
        const modName = mod.name || 'unknown';
        logger.error(
          `Can't find the modifier ${modName} with id ${mod.id} or modifier group ${modModifierGroupId} based on the id or name from the hypotheses in the menu`
        );
        const errorMessage = `can't find the modifier ${modName} with id ${mod.id} or modifier group ${modModifierGroupId} based on the ids from the hypothesis in the menu`;
        const payload: Partial<ErrorTransmissionMessage> = {
          data: { message: errorMessage },
        };
        dispatch(messagingActions.sendError(payload as any));
      }
    };

    const handleOrderItemChildren = (
      children: EntityMenuItem[],
      fullMainItem: ParsedMenuItem | undefined,
      currentCartItemId: number
    ) => {
      if (!fullMainItem) {
        logger.error(
          `Cannot handle order item children: fullMainItem is undefined`
        );
        return;
      }

      children.forEach((mod) => {
        if (mod.quantity > 1) {
          new Array(mod.quantity).fill(0).forEach((_, index) => {
            selectChildMod({
              mod,
              fullMainItem,
              currentCartItemId,
              modModifierGroupId: mod.modifier_group_id + '_' + (index + 1),
              modQuantity: 1,
            });
          });
        } else {
          selectChildMod({
            mod,
            fullMainItem,
            currentCartItemId,
            modModifierGroupId: mod.modifier_group_id,
            modQuantity: mod.quantity || 1,
          });
        }
      });
    };

    //use the first menu item with the id in the assumption of the same item will show up in the menu once
    let mainItem = Object.values(topLevelMenuItems).find((menuItem) =>
      orderItem.id !== -1
        ? String(orderItem.id) === menuItem.id
        : orderItem.name === menuItem.id
    );

    if (!mainItem) {
      // Find the item in menu by name if it couldn't be found by id
      logger.error(
        `Can't find the main item based on the id: ${orderItem.id} from the hypotheses in the menu`
      );

      mainItem = Object.values(topLevelMenuItems).find(
        (menuItem) => orderItem.name === menuItem.name
      );
    }

    if (
      mainItem &&
      !(
        isUnifiedMenu &&
        isBMItemOutOfStock(unavailableItemsByName, mainItem.name)
      )
    ) {
      let currentCartItemId = cartSequenceId;
      const inputModSymbol = orderItem.pos_specific_properties?.modcode
        ? codeNameMapping[orderItem.pos_specific_properties.modcode]?.code
        : '';

      const isItemAutoCombined = !!(
        isUnifiedMenu &&
        FEATURE_FLAG_BM_AUTO_COMBO &&
        orderItem.is_auto_combined
      );
      //add the main item to cart
      await dispatch(
        addItemToCart({
          menuItem: mainItem,
          quantity: orderItem.quantity,
          addedBy: CartItemAdditionTypes.AI,
          inputModSymbol,
          buildViaAutoCombo: isItemAutoCombined,
        })
      );

      const {
        menu: { fullMenuItems },
        cart: { cartItems },
      } = getState();

      const itemId = mainItem.id + '-' + mainItem.categoryId;
      const fullMainItem = fullMenuItems[itemId];

      if (!fullMainItem) {
        logger.error(
          `Cannot find fullMainItem for itemId: ${itemId} (mainItem: ${mainItem.name}, id: ${mainItem.id})`
        );
        // Still continue with quantity update and item selection, but skip modifier processing
      } else {
        const children = orderItem.children;
        if (children?.length > 0) {
          handleOrderItemChildren(children, fullMainItem, currentCartItemId);
        }
      }

      let quantity = orderItem.quantity ? Number(orderItem.quantity) : 1;

      if (quantity > 1) {
        dispatch(
          cartActions.updateQuantity({
            cartItemId: currentCartItemId.toString(),
            quantity: Math.min(quantity, 19),
          })
        );
      }
      dispatch(
        dialogActions.selectItemAndResetNavigation({
          item: cartItems[currentCartItemId],
          itemCartId: currentCartItemId,
        })
      );
    } else {
      logger.error(
        `Can't find the main item based on the id: ${orderItem.id} or name: ${orderItem.name} from the hypotheses in the menu`
      );
      const errorMessage = `can't find the main item ${orderItem.id} based on the id from the hypothesis in the menu`;
      const payload: Partial<ErrorTransmissionMessage> = {
        data: { message: errorMessage },
      };
      dispatch(messagingActions.sendError(payload as any));
      logger.log(
        `Add dummy hypothesis item: ${orderItem.name} to cart with cart item id: ${cartSequenceId}`
      );
      dispatch(
        addHypothesisCartItem({
          ...orderItem,
          cartItemId: cartSequenceId,
        })
      );
    }
  }
);

export const fetchMenuVersions = createAsyncThunk(
  'menu/menuVersions',
  async (
    {
      restaurantCode: restaurant_code,
      logout,
    }: { restaurantCode: string; logout: Function },
    { getState }
  ) => {
    const {
      config: { MENU_API },
    } = getState();
    let filteredMenuVersions: IMenuVersion[] = [];
    let prodLiveVersion = {} as IMenuVersion;
    if (restaurant_code) {
      try {
        const menuVersions: { data: IMenuVersionsResponse[] } =
          await getMenuVersionsFromMenuAPI(MENU_API, {
            restaurant_code,
          });
        const { LIVE, PLAYGROUND } = MenuStages;
        const versionMapping = (menuVersions?.data || []).reduce(
          (
            list,
            {
              commit_id: commitId,
              created_at: createdAt,
              creator_first_name,
              creator_last_name,
              publisher_username: publisherUsername,
              publisher_first_name,
              publisher_last_name,
              creator_username: creatorUsername,
              id,
              stage,
              is_active: isActive,
              updated_at: updatedAt,
              comment,
            }
          ) => {
            const versionData = {
              commitId,
              createdAt,
              creatorUsername,
              creatorName: `${creator_first_name} ${creator_last_name}`,
              publisherUsername,
              publisherName: `${publisher_first_name} ${publisher_last_name}`,
              id,
              stage,
              isActive,
              updatedAt,
              comment,
            };
            if (stage.toUpperCase() === LIVE && isActive) {
              prodLiveVersion = versionData;
            }
            if (stage.toUpperCase() === PLAYGROUND) {
              list[commitId] = versionData;
            }
            return list;
          },
          {} as Record<string, IMenuVersion>
        );
        filteredMenuVersions = Object.values(versionMapping).sort(
          (a, b) => Number(b.commitId) - Number(a.commitId)
        );
      } catch (error) {
        if (getAuthRefactorFeatureFlag()) {
          logout('Error while fetching menu versions. Logging out');
        }
      }
    }
    return {
      menuVersions: [
        Object.assign({}, defaultMenuVersion),
        ...filteredMenuVersions,
      ],
      prodLiveVersion,
    };
  }
);

export const selectMenuVersion = createAsyncThunk(
  'menu/selectMenuVersion',
  async (
    {
      currentMenuVersion,
      logout,
    }: { currentMenuVersion: string; logout: Function },
    { getState, dispatch }
  ) => {
    const {
      restaurant: {
        selectedRestaurantDetails: { timezone, restaurantCode },
        selectedStage: currentStage,
      },
    } = getState();

    if (restaurantCode) {
      dispatch(startLoading({}));
      dispatch(
        fetchMenu({
          currentMenuVersion,
          timezone,
          restaurantCode,
          currentStage,
          logout,
        })
      );
    }
    return currentMenuVersion;
  }
);

export const replaceWithComboItem = createAsyncThunk(
  'menu/replaceWithComboItem',
  async (
    {
      originalCartItem,
      targetItemName,
      comboSize,
      optionsToPreSelect,
    }: {
      originalCartItem: CartItem;
      targetItemName: string;
      comboSize?: ConversionSize;
      optionsToPreSelect?: string[];
    },
    { dispatch, getState }
  ) => {
    // Step 1: Get required state from Redux store
    const {
      menu: {
        topLevelMenuItems,
        menuRes,
        modSymbolMapping,
        brandMenu,
        brandMenuItemsByName,
        itemConversionBusinessRules,
        unavailableItemsByName,
        restaurantMealPeriodsByName,
      },
      cart: { modality, sequenceId: cartSequenceId },
      restaurant: {
        selectedRestaurantDetails: { restaurantCode = '', timezone },
      },
      config: {
        FEATURE_FLAG_ITEM_CONVERSION: isItemConversionEnabled,
        FEATURE_FLAG_SUPPRESS_UPSELL: suppressUpsellEnabled,
        FEATURE_FLAG_DO_NOT_UPSELL_IN_EXPRESS_MODE:
          doNotUpsellInExpressModeEnabled,
      },
    } = getState();

    const suppressUpsellsInExpressMode =
      doNotUpsellInExpressModeEnabled &&
      isExpressModeActive(restaurantMealPeriodsByName, timezone);

    // Step 2 & 3: Find and build the target substitute item (top-level or non-top-level)
    const substituteResult = buildSubstituteItem({
      targetItemName,
      topLevelMenuItems,
      brandMenu,
      brandMenuItemsByName,
      restaurantCode,
      unavailableItemsByName,
      timezone,
      restaurantMealPeriodsByName,
    });

    if (!substituteResult) {
      return;
    }

    const { parsedItem: parsedTargetItem, topLevelMenuItem: targetMenuItem } =
      substituteResult;

    const newTargetCartItem: CartItem = {
      ...parsedTargetItem,
      cartItemId: cartSequenceId,
      childModifierGroups: { ...originalCartItem.childModifierGroups }, // Preserve existing modifiers initially
      modality,
      modcode: targetMenuItem?.modcode,
      addedBy: CartItemAdditionTypes.human,
      readback: true,
      readbackName:
        targetMenuItem?.readbackName ||
        brandMenuItemsByName[targetItemName]?.readback_name ||
        '',

      // Preserve user-specific data from original item
      itemLevelMemo: originalCartItem.itemLevelMemo,
      quantity: originalCartItem.quantity,
      isUpsell: originalCartItem.isUpsell,
      isUpsellPrompt: originalCartItem.isUpsellPrompt,
      label: originalCartItem.label,
      isConverted: true, // Mark this item as converted
    } as CartItem;

    // Step 4: Filter and preserve compatible modifiers from original item
    // Only keep modifiers that exist in both original and target items
    if (
      originalCartItem.childModifierGroups &&
      newTargetCartItem.modifierGroups
    ) {
      const targetModifierGroupNames = Object.values(
        newTargetCartItem.modifierGroups
      ).map((group) => group.name?.toLowerCase());

      const compatibleModifierGroups: { [key: string]: any } = {};
      Object.entries(originalCartItem.childModifierGroups).forEach(
        ([key, modifierGroup]) => {
          const modifierGroupName = modifierGroup.name?.toLowerCase();
          if (
            modifierGroupName &&
            targetModifierGroupNames.includes(modifierGroupName)
          ) {
            compatibleModifierGroups[key] = modifierGroup;
          }
        }
      );

      // Update the cart item with only compatible modifiers
      newTargetCartItem.childModifierGroups = compatibleModifierGroups;
    }

    // Step 5: Replace items in cart (delete original, add new target item)
    dispatch(
      cartActions.deleteCartItem(originalCartItem.cartItemId.toString())
    );

    dispatch(
      cartActions.addItemToCart({
        cartItem: newTargetCartItem,
        suppressUpsellsInExpressMode,
        suppressUpsellEnabled,
      })
    );

    // Step 6: Pre-select modifiers based on conversion type (e.g., combo_size)
    if (newTargetCartItem.modifierGroups && optionsToPreSelect) {
      // Pre-select each option specified in the conversion info
      // Search through all modifier groups to find one that contains the option
      for (const optionName of optionsToPreSelect) {
        const targetModifierGroup = Object.values(
          newTargetCartItem.modifierGroups
        ).find((modGroup) =>
          Object.values(modGroup.menuItems).some(
            (item) => item.name === optionName
          )
        );

        if (targetModifierGroup) {
          const targetMenuItem = Object.values(
            targetModifierGroup.menuItems
          ).find((item) => item.name === optionName);

          if (targetMenuItem) {
            // Select the modifier
            dispatch(
              cartActions.selectModifier({
                cartItemId: newTargetCartItem.cartItemId,
                menuItemId: targetMenuItem.itemId,
                modGroupId: targetModifierGroup.id,
                selected: true,
                modCode: '',
                modSymbolMapping,
                posSettings: menuRes?.posSettings || {},
                readback: true,
                suppressUpsellEnabled,
                applyExpressCascade: suppressUpsellsInExpressMode,
              })
            );

            // Step 7: Apply option replacement if triggered by the pre-selected modifier
            // Only run option replacement if both unified menu and item conversion feature flags are enabled
            const isUnifiedMenu = getMenuUnificationFeatureFlag();
            if (isUnifiedMenu && isItemConversionEnabled) {
              // Extract option replacement business rules (default size selection + combo size transformation)
              const optionReplacementRules: IBusinessRule[] = [
                ...itemConversionBusinessRules.defaultSizeSelection,
                ...itemConversionBusinessRules.comboSizeTransformation,
              ];

              const replacementInfo = analyzeOptionReplacement(
                targetModifierGroup.name || '',
                optionName,
                newTargetCartItem,
                optionReplacementRules,
                unavailableItemsByName
              );

              if (replacementInfo.shouldApplyReplacement) {
                dispatch(
                  cartActions.applyOptionReplacement({
                    cartItemId: newTargetCartItem.cartItemId,
                    targetModifierGroups: replacementInfo.targetModifierGroups,
                    unavailableItemsByName,
                  })
                );
              }
            }
          }
        }
      }
    }

    // Step 8: Log the conversion and focus on the new target item
    logger.log(
      `Item conversion: ${originalCartItem.name} -> ${newTargetCartItem.name}${
        comboSize ? ` (${comboSize})` : ''
      } with preserved modifiers`
    );

    dispatch(
      dialogActions.selectItemAndResetNavigation({
        item: newTargetCartItem,
        itemCartId: cartSequenceId,
      })
    );

    return {
      originalCartItemId: originalCartItem.cartItemId,
      newTargetCartItem,
      comboSize,
    };
  }
);

const menuSlice = createSlice({
  name: 'menu',
  initialState,
  reducers: {
    considerTimePeriods: (
      state,
      action: PayloadAction<{
        modality: ModalityType;
        timezone: string;
        stage: string;
      }>
    ) => {
      if (state.categoriesWithTimePeriod.length && state.menuRes) {
        const { timezone, stage } = action.payload;
        const categoriesWithActiveTimePeriod = considerTimePeriodCategory(
          state.categoriesWithTimePeriod,
          timezone
        );
        state.availableCategoryWithTimePeriod =
          categoriesWithActiveTimePeriod.filter(
            (category) => !category.inactive
          );

        const { menuItems, overrides, menuItemSettings, posSettings } =
          state.menuRes;
        const { topLevelMenuItems, codeNameMapping, modSymbolMapping } =
          parseMenuResponse({
            categories: [
              ...categoriesWithActiveTimePeriod.filter(
                (category) => category.name !== AUTO_COMBOS
              ),
              ...state.alwaysAvailableCategories,
            ],
            menuRes: { menuItems, overrides, menuItemSettings, posSettings },
            persistentVoiceProps: state.persistentVoiceProps,
            unavailableItems: state.unavailableItems,
            stage,
          });
        state.topLevelMenuItems = topLevelMenuItems;
        state.codeNameMapping = codeNameMapping;
        state.modSymbolMapping = modSymbolMapping;
      }
    },
    considerTimePeriodsForUnifiedMenu: (
      state,
      action: PayloadAction<{
        timezone: string;
      }>
    ) => {
      if (state.categoriesWithTimePeriod.length && state.brandMenu) {
        const { timezone } = action.payload;
        const categoriesWithActiveTimePeriod = considerTimePeriodCategory(
          state.categoriesWithTimePeriod,
          timezone
        );
        state.availableCategoryWithTimePeriod =
          categoriesWithActiveTimePeriod.filter(
            (category) => !category.inactive
          );

        state.topLevelMenuItems = getTopLevelItems(
          [
            ...categoriesWithActiveTimePeriod.filter(
              (category) => category.name !== AUTO_COMBOS
            ),
            ...state.alwaysAvailableCategories,
          ],
          convertToMapUsingName(state.brandMenu.items),
          state.unavailableItemsByName,
          convertToMapUsingName(state.brandMenu.constraints),
          timezone
        );
      }
    },
    setFullMenuItem: (state, action: PayloadAction<ParsedMenuItem>) => {
      const item = action.payload;
      const itemId = item.itemId;
      state.fullMenuItems[itemId] = item;
    },
    updateSelectedMenuVersion: (state, { payload }: PayloadAction<string>) => {
      state.selectedMenuVersion = payload;
    },
    resetMenu: (state) => {
      state.topLevelMenuItems = {};
      state.modSymbolMapping = {};
      state.codeNameMapping = {};
      state.menuRes = undefined;
      state.persistentVoiceProps = {};
      state.categoriesWithTimePeriod = [];
      state.availableCategoryWithTimePeriod = [];
      state.alwaysAvailableCategories = [];
      state.unavailableItems = {};
      state.bmItemsSynonyms = undefined;
      state.loadedVersionDetails = null;
      state.restaurantMealPeriodsByName = {};
    },
    updateMenuItems: (state, action) => {
      const {
        topLevelMenuItems,
        processedCategoryAndTimePeriodJSON,
        processedMenuJSON,
        processedModGroupsJSON,
        categoriesWithTimePeriod,
        availableCategoryWithTimePeriod,
        alwaysAvailableCategories,
        persistentVoiceProps,
        codeNameMapping,
        modSymbolMapping,
        unavailableItems,
        autoComboPrpIds,
        comboPrpIdToALaCartePrpId,
        brandMenu,
        bmItemsSynonyms,
        restaurantUnavailableEntities,
        unavailableItemsByName,
        restaurantMealPeriodsByName,
        itemConversionBusinessRules,
        greetingDetails,
        intentCards,
      } = action.payload;
      state.topLevelMenuItems = topLevelMenuItems;
      logger.info({
        message: 'Updating top level menu items',
        itemCount: Object.keys(topLevelMenuItems).length,
      });
      state.modSymbolMapping = modSymbolMapping;
      state.codeNameMapping = codeNameMapping;
      state.menuRes = {
        ...processedCategoryAndTimePeriodJSON,
        ...processedMenuJSON,
        ...processedModGroupsJSON,
      };
      state.persistentVoiceProps = persistentVoiceProps;
      state.categoriesWithTimePeriod = categoriesWithTimePeriod;
      state.availableCategoryWithTimePeriod = availableCategoryWithTimePeriod;
      state.alwaysAvailableCategories = alwaysAvailableCategories;
      state.unavailableItems = unavailableItems;
      state.autoComboPrpIds = autoComboPrpIds;
      state.comboPrpIdToALaCartePrpId = comboPrpIdToALaCartePrpId;
      state.brandMenu = brandMenu;
      state.brandMenuItemsByName = convertToMapUsingName(
        brandMenu?.items || []
      );
      state.itemConversionBusinessRules = itemConversionBusinessRules;
      state.bmItemsSynonyms = bmItemsSynonyms;
      state.restaurantUnavailableEntities = restaurantUnavailableEntities;
      state.unavailableItemsByName = unavailableItemsByName;
      state.restaurantMealPeriodsByName = restaurantMealPeriodsByName || {};
      state.greetingDetails = greetingDetails || undefined;
      state.intentCards = intentCards || undefined;
    },
    updateIntentCards: (state, action: PayloadAction<IUtterance[]>) => {
      state.intentCards = buildIntentCardsFromBM(action.payload) || undefined;
      state.dismissRepeatPhrases =
        getDismissRepeatPhrasesFromBM(action.payload) || undefined;
    },
    resetVersions: (state) => {
      state.menuVersions = [];
      state.prodLiveVersion = {} as IMenuVersion;
    },
    updateItemSlashCommandIdx: (
      state,
      action: PayloadAction<{ itemName: string; newIndex: number }>
    ) => {
      const { itemName, newIndex } = action.payload;
      if (Object.keys(state.topLevelMenuItems).includes(itemName)) {
        state.topLevelMenuItems[itemName].slashCommandIdx = newIndex;
      }
    },
    updateLoadedVersionDetails: (
      state,
      { payload }: PayloadAction<LoadedVersionDetails | null>
    ) => {
      state.loadedVersionDetails = payload;
    },
    updateMenuLoadingTimings: (
      state,
      { payload }: PayloadAction<MenuLoadingTimings>
    ) => {
      state.menuLoadingTimings = { ...state.menuLoadingTimings, ...payload };
    },
    resetMenuLoadingTimings: (state) => {
      state.menuLoadingTimings = {};
    },
  },
  extraReducers: (builder) => {
    builder.addCase(
      fetchMenuVersions.fulfilled,
      (state, { payload: { menuVersions, prodLiveVersion } }) => {
        state.menuVersions = menuVersions;
        state.prodLiveVersion = prodLiveVersion;
      }
    );
    builder.addCase(selectMenuVersion.fulfilled, (state, action) => {
      state.selectedMenuVersion = action.payload;
    });
  },
});

export const menuActions = menuSlice.actions;
export const {
  considerTimePeriods,
  setFullMenuItem,
  updateSelectedMenuVersion,
  updateMenuItems,
  updateIntentCards,
  resetMenu,
  resetVersions,
  considerTimePeriodsForUnifiedMenu,
  updateItemSlashCommandIdx,
  updateLoadedVersionDetails,
  updateMenuLoadingTimings,
  resetMenuLoadingTimings,
} = menuSlice.actions;

export default menuSlice.reducer;
